import { GroupModals } from "@/components/ui-elements/GroupModals";
import AddFolderPopup from "@/features/list/components/AddFolderPopup";
import AddRulePopup from "@/features/list/components/AddRulePopup";

import { fetchClient } from "@/lib/fetchClient";
import { useAuthStore } from "@/store/auth/authUserStore";
import useFolderStore from "@/store/ui/folderStore";
import useWorkflowLabelStore from "@/store/workflow/workflowLabelStore";
import useWorkflowStore from "@/store/workflow/workflowStore";
import type { FormatRule } from "@/types/resources";
import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DropdownMenu,
  ModalOverlay,
  NoResults,
  SearchInput,
} from "./components";
import { useMenuData } from "./hooks/useMenuData";
import { useNavigation } from "./hooks/useNavigation";
import { useScrollPosition } from "./hooks/useScrollPosition";
import { useWorkflowMenu } from "./hooks/useWorkflowMenu";
import { WorkflowList } from "./WorkflowList";
import { FolderItem } from "./WorkflowList/FolderItem";
import { WorkflowListItem } from "./WorkflowList/WorkflowListItem";

// TODO: format_idではなく、workflow_idに変更する
export type MenuType = {
  key: number;
  label: string;
  open?: boolean;
  format_id: string;
  set_train_data?: boolean;
  use_llm?: boolean;
  is_full_text_ocr?: boolean;
  setting_menu_active: boolean;
  format_rule_item: FormatRule | null;
  workflow_type: "ocr" | "sorting";
  sorting_executable: boolean;
};

export const ListItems = () => {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === "superadmin";
  const navigate = useNavigate();
  const { workflow_id } = useParams<{
    workflow_id: string;
  }>();
  // スクロール処理が実行されたかを追跡するref
  const initialScrollCompletedRef = useRef(false);

  const {
    openAddRulePopup,
    openDeleteGroupPopup,
    openCloneGroupPopup,
    isAddRulePopupOpen,
    isDeleteGroupPopupOpen,
    isCloneGroupPopupOpen,
    selectedWorkflowId,
  } = useWorkflowStore();

  const { setSelectedWorkflowLabel, selectedWorkflowLabel } =
    useWorkflowLabelStore();
  const {
    searchTerm,
    handleSearchChange,
    filteredFolders,
    filteredUnassignedWorkflows,
  } = useMenuData();

  const {
    menuOpen,
    setMenuOpen,
    menuTriggerElement,
    selectedWorkflow,
    handleMenuClick,
    handleMenuClose,
  } = useWorkflowMenu();

  const { handleClickMenuItem } = useNavigation();

  const { saveScrollPosition, scrollToWorkflowItem } = useScrollPosition();
  const { openFolder } = useFolderStore();

  // URLから取得したworkflow_idに基づいて関連フォルダを開く処理（初回のみ）
  useEffect(() => {
    if (
      workflow_id &&
      !initialScrollCompletedRef.current &&
      filteredFolders.length > 0
    ) {
      // フォルダ内のルールからworkflow_idが一致するフォルダを開く
      for (const folder of filteredFolders) {
        const workflowExists = folder.workflows.some(
          (workflow) => workflow.id === workflow_id
        );
        if (workflowExists) {
          openFolder(folder.id);
          break;
        }
      }

      // 少し遅延してからスクロール
      setTimeout(() => {
        scrollToWorkflowItem(workflow_id);
        initialScrollCompletedRef.current = true;
      }, 100);
    }
  }, [workflow_id, filteredFolders, openFolder]);

  // URLが変更されたら初期化
  useEffect(() => {
    return () => {
      initialScrollCompletedRef.current = false;
    };
  }, [workflow_id]);

  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  const handleRouterPush = (workflow_id: string) => {
    navigate(`/${workflow_id}/process/documentSetting`);
  };

  const handleAddRule = () => {
    openAddRulePopup();
    handleRouterTop();
    setCreateMenuOpen(false);
    handleMenuClose();
  };

  const [isAddFolderPopupOpen, setIsAddFolderPopupOpen] = useState(false);

  const handleAddFolder = () => {
    setIsAddFolderPopupOpen(true);
    handleRouterTop();
    setCreateMenuOpen(false);
    handleMenuClose();
  };

  const handleCloseAddFolderPopup = () => {
    setIsAddFolderPopupOpen(false);
  };

  const location = useLocation();

  const handleRouterTop = () => {
    if (location.pathname === `/${workflow_id}/process/documentSetting`) {
      saveScrollPosition();
      navigate(`/views/${workflow_id}`, { replace: true });
    }
  };

  const handleSettingClick = (menuItem: MenuType) => {
    handleMenuClose();
    saveScrollPosition();
    if (menuItem.workflow_type === "ocr") {
      handleClickMenuItem("/workflow/" + menuItem.format_id);
      return;
    }
    handleRouterPush(menuItem.format_id);
    setSelectedWorkflowLabel(menuItem.label);
  };

  const handleDeleteClick = (menuItem: MenuType) => {
    openDeleteGroupPopup(menuItem.format_id);
    setSelectedWorkflowLabel(menuItem.label);
    handleMenuClose();
  };

  const handleCloneClick = (menuItem: MenuType) => {
    openCloneGroupPopup(menuItem.format_id);
    setSelectedWorkflowLabel(menuItem.label);
    handleMenuClose();
  };

  const handleExportClick = async (menuItem: MenuType) => {
    const { data, response } = await fetchClient.GET(
      "/workflows/{workflow_id}/export",
      {
        params: { path: { workflow_id: menuItem.format_id } },
        parseAs: "blob",
      }
    );
    if (data === undefined) {
      throw new Error("Failed to export workflow");
    }
    const mineType = response.headers.get("content-type");
    const blob = new Blob([data], { type: mineType ?? "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    // ファイル名に使用できない文字はアンダースコアに置換する
    const filename = `${menuItem.label}.json`
      .replace(/[<>:"\/\\|?*\x00-\x1F]/g, "_")
      .trim();
    a.download = filename;
    a.href = url;
    a.click();
    handleMenuClose();
  };

  const isShowExport = (() => {
    if (selectedWorkflow === null) return false;
    // ocr ではない場合は非表示
    if (selectedWorkflow.workflow_type !== "ocr") return false;
    // 汎用モデルを選択していない場合は非表示
    if (selectedWorkflow.format_rule_item === null) return false;
    // 汎用モデル「テキスト」の場合は非表示
    if (selectedWorkflow.is_full_text_ocr) return false;
    // 汎用モデル「表形式」の場合は非表示
    if (!selectedWorkflow.use_llm) return false;
    // ログインユーザの権限がsuperadminでない場合は非表示
    if (!isSuperAdmin) return false;
    return true;
  })();

  // 新規作成メニューのアイテム
  const createMenuItems = [
    {
      label: "ルールの作成",
      onClick: handleAddRule,
    },
    {
      label: "フォルダの追加",
      onClick: handleAddFolder,
    },
  ];

  // ワークフローメニューのアイテム
  const getWorkflowMenuItems = () => {
    if (!selectedWorkflow) return [];

    const items: Array<{
      label: string;
      onClick: () => void;
      color?: string;
    }> = [
      {
        label: "設定",
        onClick: () => handleSettingClick(selectedWorkflow),
      },
      {
        label: "複製",
        onClick: () => handleCloneClick(selectedWorkflow),
      },
    ];

    if (isShowExport) {
      items.push({
        label: "設定をエクスポート",
        onClick: () => handleExportClick(selectedWorkflow),
      });
    }

    items.push({
      label: "削除",
      onClick: () => handleDeleteClick(selectedWorkflow),
      color: "#FF0000",
    });

    return items;
  };

  // メニューの表示位置を計算
  const calculateMenuPosition = () => {
    if (!menuTriggerElement) return {};

    const triggerRect = menuTriggerElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const bottomSpace = windowHeight - triggerRect.bottom;
    const menuHeight = 140;

    // 画面下部との距離が想定メニュー高さより小さい場合は上に表示
    if (bottomSpace < menuHeight) {
      return {
        position: "absolute" as const,
        top: `${triggerRect.top - menuHeight}px`,
        left: `${triggerRect.left}px`,
      };
    }

    // 十分なスペースがある場合は下に表示
    return {
      position: "absolute" as const,
      top: `${triggerRect.bottom + 5}px`,
      left: `${triggerRect.left}px`,
    };
  };

  return (
    <Fragment>
      <div className="m-4 bg-bg-white rounded-2xl w-sidemenu h-sidemenu shadow-card">
        <div className="p-4">
          <div className="flex justify-between items-center mb-2 relative">
            <h6 className="font-bold text-lg m-0 font-sans">ルール一覧</h6>
            <div>
              <div className="relative z-[100] inline-block">
                <DropdownMenu
                  open={createMenuOpen}
                  onOpenChange={setCreateMenuOpen}
                  items={createMenuItems}
                  trigger={
                    <button
                      className={`font-bold text-base border-none py-1.5 px-3 cursor-pointer font-sans normal-case relative z-[1] rounded transition-colors ${
                        createMenuOpen ? "bg-hover-blue" : "bg-transparent"
                      }`}
                      style={{ color: "#00B2FF" }}
                    >
                      新規作成
                    </button>
                  }
                />
              </div>
            </div>
          </div>
          <div className="mb-2">
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="ルールを検索"
            />
          </div>
          <div
            className="h-workflow-list overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            id="workflowList"
          >
            <div className="mb-2"></div>

            {/* 検索結果が0件の場合のメッセージ表示 */}
            {searchTerm.trim() !== "" &&
            filteredFolders.length === 0 &&
            filteredUnassignedWorkflows.length === 0 ? (
              <NoResults searchTerm={searchTerm} />
            ) : (
              <WorkflowList
                folders={filteredFolders}
                workflows={filteredUnassignedWorkflows}
                folderItemRenderer={(props) => <FolderItem {...props} />}
                workflowItemRenderer={({ workflow }) => (
                  <WorkflowListItem
                    workflow={workflow}
                    onClickMenu={handleMenuClick}
                  />
                )}
              />
            )}
          </div>

          {/* ワークフローメニュー */}
          {menuOpen && selectedWorkflow && menuTriggerElement && (
            <DropdownMenu
              open={menuOpen}
              onOpenChange={setMenuOpen}
              items={getWorkflowMenuItems()}
              trigger={<span style={{ display: "none" }}></span>}
              sideOffset={5}
              align="start"
              contentStyle={calculateMenuPosition()}
            />
          )}
        </div>
      </div>
      {isAddRulePopupOpen && (
        <GroupModals
          isAddRulePopupOpen={isAddRulePopupOpen}
          isDeleteGroupPopupOpen={isDeleteGroupPopupOpen}
          isCloneGroupPopupOpen={isCloneGroupPopupOpen}
          workflow_id={workflow_id}
          selectedWorkflowId={selectedWorkflowId}
          selectedWorkflowLabel={selectedWorkflowLabel}
        />
      )}
      {isAddFolderPopupOpen && (
        <ModalOverlay onClose={handleCloseAddFolderPopup}>
          <AddFolderPopup onClose={handleCloseAddFolderPopup} />
        </ModalOverlay>
      )}
      <AddRulePopup />
    </Fragment>
  );
};
